import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Public Turnstile site key (site keys are not secret). When it is not
// configured, the widget renders nothing and forms submit without a token —
// the server likewise only enforces verification once its secret is set.
export const TURNSTILE_SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile'));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

interface TurnstileWidgetProps {
  /** Called whenever the token changes (fresh token, or null when expired/errored). */
  onToken: (token: string | null) => void;
  /**
   * Increment this counter to reset the widget and get a fresh token —
   * Turnstile tokens are single-use, so reset after every submission attempt.
   */
  resetSignal?: number;
  className?: string;
}

/**
 * Invisible/managed Cloudflare Turnstile widget. Renders nothing when the
 * site key is not configured.
 */
export const TurnstileWidget = ({ onToken, resetSignal = 0, className }: TurnstileWidgetProps) => {
  const { i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // Reset for a fresh token after each submission attempt (tokens are single-use).
  useEffect(() => {
    if (resetSignal > 0 && widgetIdRef.current && window.turnstile) {
      onTokenRef.current(null);
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* widget gone */
      }
    }
  }, [resetSignal]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return;
    let widgetId: string | null = null;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          language: i18n.language?.startsWith('ar') ? 'ar' : 'en',
          appearance: 'interaction-only',
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
        widgetIdRef.current = widgetId;
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      if (widgetIdRef.current === widgetId) widgetIdRef.current = null;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* already removed */
        }
      }
    };
  }, [i18n.language]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
};
